/**
 * ToastContext Module
 * 
 * This module provides a context for displaying toast notifications throughout the application.
 * It wraps the react-toastify library to provide a consistent interface for showing
 * success, error, info, and warning messages.
 */
import React, { createContext, useContext } from 'react';
import { toast as toastify } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Create a context for toast notifications
const ToastContext = createContext();

/**
 * ToastProvider component
 * Provides toast notification functions to children components
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const ToastProvider = ({ children }) => {
  // Toast methods that wrap react-toastify functions
  const toast = {
    /**
     * Display a success toast notification
     * @param {string} message - Message to display
     */
    success: (message) => {
      toastify.success(message);
    },
    /**
     * Display an error toast notification
     * @param {string} message - Message to display
     */
    error: (message) => {
      toastify.error(message);
    },
    /**
     * Display an info toast notification
     * @param {string} message - Message to display
     */
    info: (message) => {
      toastify.info(message);
    },
    /**
     * Display a warning toast notification
     * @param {string} message - Message to display
     */
    warning: (message) => {
      toastify.warning(message);
    },
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
    </ToastContext.Provider>
  );
};

/**
 * Custom hook to use the toast context
 * @returns {Object} Toast context value with notification methods
 * @throws {Error} If used outside of ToastProvider
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

/**
 * Direct toast utility object for use without hooks
 * Provides the same interface as the context but can be imported directly
 */
export const toast = {
  /**
   * Display a success toast notification
   * @param {string} message - Message to display
   */
  success: (message) => toastify.success(message),
  /**
   * Display an error toast notification
   * @param {string} message - Message to display
   */
  error: (message) => toastify.error(message),
  /**
   * Display an info toast notification
   * @param {string} message - Message to display
   */
  info: (message) => toastify.info(message),
  /**
   * Display a warning toast notification
   * @param {string} message - Message to display
   */
  warning: (message) => toastify.warning(message),
};
